import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Repeat, PlayCircle, Trash2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, COMPLIANCE_COLORS, PRIORITY_STYLES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["GST", "ITR", "TDS", "ROC", "Audit", "PF", "ESI", "Custom"];
const FREQS = ["once", "monthly", "quarterly", "half-yearly", "yearly"];
const PRIOS = ["low", "medium", "high", "urgent"];

const EMPTY = {
    client_id: "",
    compliance_type: "GST",
    title: "",
    description: "",
    frequency: "monthly",
    official_due_date: "",
    internal_due_date: "",
    recurring: true,
    priority: "medium",
};

export default function Compliance() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [presetOpen, setPresetOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [presetClient, setPresetClient] = useState("");
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [applying, setApplying] = useState(false);

    const load = () => api.get("/compliance").then((r) => setItems(r.data));

    useEffect(() => {
        load();
        api.get("/clients").then((r) => setClients(r.data.items)).catch(() => {});
        api.get("/compliance/templates").then((r) => setTemplates(r.data)).catch(() => {});
    }, []);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    const applyPreset = async () => {
        if (!presetClient || selectedCodes.length === 0) {
            toast.error("Pick a client and at least one preset.");
            return;
        }
        setApplying(true);
        try {
            const { data } = await api.post("/compliance/apply-template", {
                client_id: presetClient,
                codes: selectedCodes,
            });
            toast.success(`Added ${data.created} compliance rule${data.created === 1 ? "" : "s"}`);
            setPresetOpen(false);
            setSelectedCodes([]);
            setPresetClient("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setApplying(false);
        }
    };

    const save = async () => {
        try {
            await api.post("/compliance", form);
            toast.success("Compliance added");
            setOpen(false);
            setForm(EMPTY);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const generate = async (id) => {
        try {
            await api.post(`/compliance/${id}/generate-task`);
            toast.success("Task generated from compliance");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this compliance rule?")) return;
        await api.delete(`/compliance/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="compliance-page">
            <PageHeader
                title="Compliance"
                subtitle="Recurring compliance rules that generate tasks on schedule"
                actions={
                    isAdmin && (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedCodes([]);
                                    setPresetClient("");
                                    setPresetOpen(true);
                                }}
                                data-testid="preset-btn"
                            >
                                <Sparkles size={15} className="mr-1.5" /> Apply preset
                            </Button>
                            <Button
                                onClick={() => {
                                    setForm(EMPTY);
                                    setOpen(true);
                                }}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="new-compliance-btn"
                            >
                                <Plus size={16} className="mr-1.5" /> Add compliance
                            </Button>
                        </>
                    )
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((c) => (
                    <Card
                        key={c.id}
                        className="p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
                        data-testid={`compliance-card-${c.id}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <span className={`badge-status ${COMPLIANCE_COLORS[c.compliance_type]}`}>
                                    {c.compliance_type}
                                </span>
                                <h3 className="font-display font-semibold text-slate-800 mt-2">
                                    {c.title}
                                </h3>
                                <div className="text-xs text-slate-500 mt-1">
                                    {clientName(c.client_id)}
                                </div>
                            </div>
                            <span className={`badge-status ${PRIORITY_STYLES[c.priority]}`}>
                                {c.priority}
                            </span>
                        </div>
                        <div className="mt-4 space-y-1.5 text-sm">
                            <div className="flex justify-between">
                                <span className="text-slate-500 text-xs">Official due</span>
                                <span className="text-slate-700 font-medium">{fmtDate(c.official_due_date)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500 text-xs">Frequency</span>
                                <span className="text-slate-700 capitalize inline-flex items-center gap-1">
                                    {c.recurring && <Repeat size={11} />} {c.frequency}
                                </span>
                            </div>
                        </div>
                        {isAdmin && (
                            <div className="mt-4 flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => generate(c.id)}
                                    className="flex-1"
                                    data-testid={`gen-task-${c.id}`}
                                >
                                    <PlayCircle size={13} className="mr-1" /> Generate task
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => remove(c.id)}
                                    className="text-rose-600 hover:bg-rose-50"
                                >
                                    <Trash2 size={13} />
                                </Button>
                            </div>
                        )}
                    </Card>
                ))}
                {items.length === 0 && (
                    <div className="text-slate-500 text-sm">No compliance rules yet.</div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Add compliance</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <F label="Title">
                                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                            </F>
                        </div>
                        <F label="Client">
                            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                                <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Type">
                            <Select value={form.compliance_type} onValueChange={(v) => setForm({ ...form, compliance_type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Frequency">
                            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {FREQS.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Priority">
                            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PRIOS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Official due date">
                            <Input type="date" value={form.official_due_date} onChange={(e) => setForm({ ...form, official_due_date: e.target.value })} />
                        </F>
                        <F label="Internal due date">
                            <Input type="date" value={form.internal_due_date || ""} onChange={(e) => setForm({ ...form, internal_due_date: e.target.value })} />
                        </F>
                        <div className="col-span-2 flex items-center gap-3">
                            <Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} />
                            <span className="text-sm text-slate-700">
                                Recurring — auto-roll to next period when a task is generated
                            </span>
                        </div>
                        <div className="col-span-2">
                            <F label="Description">
                                <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </F>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-compliance">
                            Add
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preset templates dialog */}
            <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            Apply government preset
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-500">
                        Choose a client and one or more preset filings. Due dates are
                        auto-computed from today; you can fine-tune later.
                    </p>
                    <div className="mt-2">
                        <F label="Client">
                            <Select value={presetClient} onValueChange={setPresetClient}>
                                <SelectTrigger data-testid="preset-client">
                                    <SelectValue placeholder="Choose client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                        {templates.map((t) => {
                            const on = selectedCodes.includes(t.code);
                            return (
                                <button
                                    type="button"
                                    key={t.code}
                                    onClick={() =>
                                        setSelectedCodes((s) =>
                                            on ? s.filter((c) => c !== t.code) : [...s, t.code],
                                        )
                                    }
                                    className={`text-left p-3 border rounded-md transition-colors ${
                                        on
                                            ? "border-primary bg-blue-50/60"
                                            : "border-slate-200 hover:border-slate-300"
                                    }`}
                                    data-testid={`preset-${t.code}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`badge-status ${
                                                COMPLIANCE_COLORS[t.compliance_type] || ""
                                            }`}
                                        >
                                            {t.compliance_type}
                                        </span>
                                        <span className="text-xs text-slate-500 capitalize">
                                            {t.frequency}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 text-sm font-medium text-slate-800">
                                        {t.label}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Next due: {t.preview_due_date}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPresetOpen(false)}>Cancel</Button>
                        <Button
                            onClick={applyPreset}
                            disabled={applying}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="apply-preset"
                        >
                            {applying && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                            Add {selectedCodes.length || ""} rule{selectedCodes.length === 1 ? "" : "s"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
